'use client'

import { cva } from 'class-variance-authority'
import { type ReactNode, Children, createContext, useContext } from 'react'

import { cn, passPropsToSingleChild } from '@/shared/lib'

import { Separator } from '../separator'

type StepperContextValue = {
  variant: 'primary' | 'secondary'
  orientation: 'vertical' | 'horizontal'
  size: 'small' | 'medium'
  activeStep?: string | number
  allActive: boolean
}

const StepperContext = createContext<StepperContextValue | null>(null)

const useStepperContext = () => {
  const context = useContext(StepperContext)

  if (!context) {
    throw new Error(
      'Stepper components must be used within <SquareStepper.Root>',
    )
  }

  return context
}

const rootVariants = cva('relative flex', {
  variants: {
    variant: {
      primary: 'bg-white',
      secondary: 'bg-gray-0 gap-2',
    },
    orientation: {
      vertical: 'flex-col',
      horizontal: 'flex-row',
    },
    size: {
      small: '',
      medium: '',
    },
  },
  compoundVariants: [
    {
      variant: 'secondary',
      orientation: 'horizontal',
      size: 'small',
      className: 'px-4 py-6',
    },
    {
      variant: 'secondary',
      orientation: 'horizontal',
      size: 'medium',
      className: 'p-[22.5px_40px_13.5px]',
    },
  ],
})

type RootProps = {
  variant?: 'primary' | 'secondary'
  orientation?: 'vertical' | 'horizontal'
  size?: 'small' | 'medium'
  activeStep?: string | number
  allActive?: boolean
  className?: string
  lineClassName?: string
  children?: ReactNode
}

export const Root = ({
  variant = 'primary',
  orientation = 'vertical',
  size = 'medium',
  activeStep,
  allActive = false,
  className,
  lineClassName,
  children,
}: RootProps) => {
  const steps = Children.toArray(children)

  const value = { variant, orientation, size, activeStep, allActive }

  const isSecondary = variant === 'secondary'

  return (
    <StepperContext.Provider value={value}>
      <ol
        className={cn(rootVariants({ variant, orientation, size, className }))}
      >
        {isSecondary && <Line className={lineClassName} />}
        {steps.map((step, index) =>
          passPropsToSingleChild(step, {
            index,
            stepLength: steps.length,
          }),
        )}
      </ol>
    </StepperContext.Provider>
  )
}

const lineVariants = cva('absolute', {
  variants: {
    variant: {
      primary: 'bg-black',
      secondary: 'bg-white',
    },
    orientation: {
      vertical: '',
      horizontal: '',
    },
    size: {
      small: '',
      medium: '',
    },
  },
  compoundVariants: [
    {
      variant: 'primary',
      orientation: 'horizontal',
      className: 'h-px',
    },
    {
      variant: 'primary',
      orientation: 'vertical',
      className: 'w-px',
    },
    {
      variant: 'secondary',
      orientation: 'horizontal',
      size: 'small',
      className: 'top-[39px] right-4 left-4 h-1',
    },
    {
      variant: 'secondary',
      orientation: 'horizontal',
      size: 'medium',
      className: 'top-13 right-10 left-10 h-1',
    },
    {
      variant: 'secondary',
      orientation: 'vertical',
      className: 'w-1',
    },
  ],
})

type LineProps = {
  className?: string
}

export const Line = ({ className }: LineProps) => {
  const { variant, orientation, size } = useStepperContext()

  return (
    <div
      className={cn(lineVariants({ variant, orientation, size, className }))}
    />
  )
}

const StepContext = createContext<{ active: boolean }>({ active: false })

const useStepContext = () => useContext(StepContext)

const stepVariants = cva('relative flex flex-1', {
  variants: {
    variant: {
      primary: 'items-start',
      secondary: 'items-center',
    },
    orientation: {
      vertical: 'flex-row gap-4',
      horizontal: 'flex-col',
    },
    size: {
      small: 'p-0',
      medium: '',
    },
  },
  compoundVariants: [
    {
      orientation: 'horizontal',
      size: 'small',
      className: 'gap-2',
    },
    {
      orientation: 'horizontal',
      size: 'medium',
      className: 'gap-3',
    },
    {
      variant: 'secondary',
      size: 'medium',
      className: 'pt-3 pb-4',
    },
  ],
})

type StepProps = {
  stepKey?: string
  className?: string
  index?: number
  stepLength?: number
  children: ReactNode
}

export const Step = ({
  stepKey: stepKeyProp,
  index = 0,
  stepLength = 0,
  className,
  children,
}: StepProps) => {
  const { variant, orientation, size, activeStep, allActive } =
    useStepperContext()

  const stepNumber = index + 1
  const stepKey = stepKeyProp ?? index
  const active = allActive || activeStep === stepKey

  const isPrimary = variant === 'primary'
  const isHorizontal = orientation === 'horizontal'

  const showSeparator = isPrimary && (isHorizontal || index !== stepLength - 1)

  return (
    <StepContext.Provider value={{ active }}>
      <li
        className={cn(stepVariants({ variant, orientation, size, className }))}
      >
        <div
          className={cn(
            'flex',
            isPrimary &&
              (isHorizontal
                ? 'w-full items-center'
                : 'flex-col items-center self-stretch'),
          )}
        >
          <StepMarker stepNumber={stepNumber} />
          {showSeparator && (
            <Separator
              color="black"
              orientation={orientation}
              className="flex-1 bg-black"
            />
          )}
        </div>
        {children}
      </li>
    </StepContext.Provider>
  )
}

const stepMarkerVariants = cva(
  'm-0.5 flex items-center justify-center border-2 border-transparent bg-white font-[800] text-gray-200',
  {
    variants: {
      active: {
        true: 'm-0 border-4 border-white bg-black text-white outline-2 -outline-offset-2 outline-black',
      },
      size: {
        small: 'text-[15px]',
        medium: 'text-[20px]',
      },
    },
    compoundVariants: [
      {
        size: 'small',
        active: true,
        className: 'h-8.5 w-8.5 rounded-lg',
      },
      {
        size: 'small',
        active: false,
        className: 'h-7.5 w-7.5 rounded-[6px]',
      },
      {
        size: 'medium',
        active: true,
        className: 'h-11 w-11 rounded-[10px]',
      },
      {
        size: 'medium',
        active: false,
        className: 'h-10 w-10 rounded-lg',
      },
    ],
  },
)

type StepMarkerProps = {
  stepNumber: number
}

const StepMarker = ({ stepNumber }: StepMarkerProps) => {
  const { size } = useStepperContext()
  const { active } = useStepContext()

  return (
    <div className={cn(stepMarkerVariants({ size, active }))}>{stepNumber}</div>
  )
}

const contentVariants = cva('space-y-2', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
    },
    size: {
      small: 'space-y-2',
      medium: 'space-y-4',
    },
  },
})

type ContentProps = {
  className?: string
  children: ReactNode
}

export const Content = ({ className, children }: ContentProps) => {
  const { variant, size } = useStepperContext()

  return (
    <div className={cn(contentVariants({ variant, size, className }))}>
      {children}
    </div>
  )
}

const labelVariants = cva('', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
    },
    active: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'secondary',
      active: true,
      className: 'font-bold text-black',
    },
    {
      variant: 'secondary',
      active: false,
      className: 'font-medium text-gray-300',
    },
  ],
})

type LabelProps = {
  className?: string
  children: ReactNode
}

export const Label = ({ className, children }: LabelProps) => {
  const { variant } = useStepperContext()
  const { active } = useStepContext()

  return (
    <h3 className={cn(labelVariants({ variant, active, className }))}>
      {children}
    </h3>
  )
}

const DescriptionVariants = cva('font-normal whitespace-break-spaces', {
  variants: {
    variant: {
      primary: '',
      secondary: '',
    },
    size: {
      small: '',
      medium: '',
    },
    color: {
      black: 'text-gray-900',
      gray: 'text-gray-600',
    },
  },
  compoundVariants: [
    {
      variant: 'primary',
      size: 'small',
      className: 'typo-body2',
    },
    {
      variant: 'primary',
      size: 'medium',
      className: 'typo-body1',
    },
  ],
})

type DescriptionProps = {
  color?: 'black' | 'gray'
  className?: string
  children: ReactNode
}

export const Description = ({
  color = 'black',
  className,
  children,
}: DescriptionProps) => {
  const { variant, size } = useStepperContext()

  return (
    <p className={cn(DescriptionVariants({ variant, size, color, className }))}>
      {children}
    </p>
  )
}
